import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import APILogsViewer from '@/components/APILogsViewer';
import { 
  Key, 
  Plus, 
  RefreshCw, 
  Ban, 
  RotateCcw, 
  Copy, 
  ArrowLeft,
  Check,
  AlertCircle,
  Calendar,
  Activity,
  Shield,
  FileText
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminAPIManagement = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [apiSettings, setApiSettings] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [newKeyData, setNewKeyData] = useState(null);
  const [copiedKey, setCopiedKey] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    rate_limit_per_minute: 60,
    daily_lead_cap: '',
    allowed_ips: '',
    expires_in_days: ''
  });

  useEffect(() => {
    if (tenantId) {
      fetchAPISettings();
      fetchAPIKeys();
    }
  }, [tenantId]);

  const fetchAPISettings = async () => {
    try {
      const response = await axios.get(`${API}/saas-admin/tenants/${tenantId}/api-settings`, {
        withCredentials: true
      });
      setApiSettings(response.data);
    } catch (error) {
      toast.error('Failed to load API settings');
      console.error(error);
    }
  };

  const fetchAPIKeys = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/saas-admin/tenants/${tenantId}/api-keys`, {
        withCredentials: true
      });
      setApiKeys(response.data.api_keys || []);
    } catch (error) {
      toast.error('Failed to load API keys');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAPIAccess = async (enabled) => {
    try {
      await axios.put(
        `${API}/saas-admin/tenants/${tenantId}/api-settings`,
        { api_access_enabled: enabled },
        { withCredentials: true }
      );
      toast.success(`API access ${enabled ? 'enabled' : 'disabled'}`);
      fetchAPISettings();
    } catch (error) {
      toast.error('Failed to update API access');
      console.error(error);
    }
  };

  const createAPIKey = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        name: formData.name,
        rate_limit_per_minute: parseInt(formData.rate_limit_per_minute),
        daily_lead_cap: formData.daily_lead_cap ? parseInt(formData.daily_lead_cap) : null,
        allowed_ips: formData.allowed_ips ? formData.allowed_ips.split(',').map(ip => ip.trim()) : [],
        expires_in_days: formData.expires_in_days ? parseInt(formData.expires_in_days) : null
      };

      const response = await axios.post(
        `${API}/saas-admin/tenants/${tenantId}/api-keys`,
        payload,
        { withCredentials: true }
      );

      setNewKeyData(response.data);
      setShowCreateDialog(false);
      setShowKeyModal(true);
      
      // Reset form
      setFormData({
        name: '',
        rate_limit_per_minute: 60,
        daily_lead_cap: '',
        allowed_ips: '',
        expires_in_days: ''
      });
      
      fetchAPIKeys();
      fetchAPISettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create API key');
      console.error(error);
    }
  };

  const revokeAPIKey = async (keyId, keyName) => {
    if (!window.confirm(`Are you sure you want to revoke the API key "${keyName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.post(
        `${API}/saas-admin/api-keys/${keyId}/revoke`,
        {},
        { withCredentials: true }
      );
      toast.success('API key revoked');
      fetchAPIKeys();
      fetchAPISettings();
    } catch (error) {
      toast.error('Failed to revoke API key');
      console.error(error);
    }
  };

  const rotateAPIKey = async (keyId, keyName) => {
    if (!window.confirm(`Rotate API key "${keyName}"? The old key will be revoked and a new one created.`)) {
      return;
    }

    try {
      const response = await axios.post(
        `${API}/saas-admin/api-keys/${keyId}/rotate`,
        {},
        { withCredentials: true }
      );
      
      setNewKeyData(response.data.new_key);
      setShowKeyModal(true);
      toast.success('API key rotated successfully');
      fetchAPIKeys();
    } catch (error) {
      toast.error('Failed to rotate API key');
      console.error(error);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(true);
      toast.success('API key copied to clipboard');
      setTimeout(() => setCopiedKey(false), 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (loading && !apiSettings) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate('/saas-admin/tenants')}
            className="mb-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tenants
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
            API Access Management
          </h1>
          {apiSettings && (
            <p className="text-sm sm:text-base text-slate-600 mt-1">
              Tenant: <span className="font-semibold">{apiSettings.tenant_name}</span>
            </p>
          )}
        </div>
      </div>

      {/* Tabs for API Management and Logs */}
      <Tabs defaultValue="keys" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="keys">
            <Key className="w-4 h-4 mr-2" />
            API Keys & Settings
          </TabsTrigger>
          <TabsTrigger value="logs">
            <FileText className="w-4 h-4 mr-2" />
            Logs & Audit Trail
          </TabsTrigger>
        </TabsList>

        <TabsContent value="keys" className="space-y-6 mt-6">
          {/* API Access Toggle */}
          {apiSettings && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  API Access Control
                </CardTitle>
                <CardDescription>
                  Enable or disable Lead Input API access for this tenant
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="font-medium">API Access Status</p>
                    <p className="text-sm text-slate-600">
                      {apiSettings.api_access_enabled ? (
                        <span className="text-green-600 flex items-center gap-1">
                          <Check className="w-4 h-4" />
                          Enabled - Tenant can use API keys
                        </span>
                      ) : (
                        <span className="text-red-600 flex items-center gap-1">
                          <Ban className="w-4 h-4" />
                          Disabled - All API requests will be rejected
                        </span>
                      )}
                    </p>
                  </div>
                  <Switch
                    checked={apiSettings.api_access_enabled}
                    onCheckedChange={toggleAPIAccess}
                  />
                </div>
                
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-indigo-600">{apiSettings.api_keys_count}</p>
                    <p className="text-sm text-slate-600">Total Keys</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{apiSettings.active_keys_count}</p>
                    <p className="text-sm text-slate-600">Active Keys</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* API Keys List */}
          <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>API Keys</CardTitle>
              <CardDescription>Manage API keys for this tenant</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button onClick={fetchAPIKeys} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
              <Button onClick={() => setShowCreateDialog(true)} size="sm">
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Create API Key</span>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {apiKeys.length === 0 ? (
            <div className="text-center py-12">
              <Key className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No API keys created yet</p>
              <Button onClick={() => setShowCreateDialog(true)} className="mt-4">
                Create First API Key
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden md:table-cell">Key Prefix</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden lg:table-cell">Rate Limit</TableHead>
                    <TableHead className="hidden lg:table-cell">Daily Cap</TableHead>
                    <TableHead className="hidden xl:table-cell">Last Used</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {apiKeys.map((key) => (
                    <TableRow key={key.id}>
                      <TableCell className="font-medium">
                        {key.name}
                        <div className="md:hidden text-xs text-slate-500 mt-1">
                          {key.key_prefix}
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <code className="text-xs bg-slate-100 px-2 py-1 rounded">
                          {key.key_prefix}...
                        </code>
                      </TableCell>
                      <TableCell>
                        <Badge variant={key.status === 'active' ? 'default' : 'secondary'}>
                          {key.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {key.rate_limit_per_minute}/min
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {key.daily_lead_cap || 'Unlimited'}
                        {key.today_usage !== undefined && (
                          <div className="text-xs text-slate-500">
                            Today: {key.today_usage}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="hidden xl:table-cell text-sm">
                        {formatDate(key.last_used_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {key.status === 'active' && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => rotateAPIKey(key.id, key.name)}
                                title="Rotate Key"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => revokeAPIKey(key.id, key.name)}
                                title="Revoke Key"
                              >
                                <Ban className="w-4 h-4 text-red-600" />
                              </Button>
                            </>
                          )}
                          {key.status === 'revoked' && (
                            <span className="text-xs text-slate-500 px-2">Revoked</span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

        </TabsContent>

        <TabsContent value="logs" className="mt-6">
          <APILogsViewer tenantId={tenantId} />
        </TabsContent>
      </Tabs>

      {/* Create API Key Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
            <DialogDescription>
              Create a new API key for this tenant. The key will be shown only once.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createAPIKey} className="space-y-4">
            <div>
              <Label htmlFor="name">Key Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Production Integration"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="rate_limit">Rate Limit (requests/minute) *</Label>
              <Input
                id="rate_limit"
                type="number"
                value={formData.rate_limit_per_minute}
                onChange={(e) => setFormData({...formData, rate_limit_per_minute: e.target.value})}
                min="1"
                max="1000"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="daily_cap">Daily Lead Cap (optional)</Label>
              <Input
                id="daily_cap"
                type="number"
                value={formData.daily_lead_cap}
                onChange={(e) => setFormData({...formData, daily_lead_cap: e.target.value})}
                placeholder="Leave empty for unlimited"
                min="1"
              />
            </div>
            
            <div>
              <Label htmlFor="allowed_ips">Allowed IPs (optional)</Label>
              <Input
                id="allowed_ips"
                value={formData.allowed_ips}
                onChange={(e) => setFormData({...formData, allowed_ips: e.target.value})}
                placeholder="192.168.1.1, 10.0.0.1 (comma-separated)"
              />
              <p className="text-xs text-slate-500 mt-1">Leave empty to allow all IPs</p>
            </div>
            
            <div>
              <Label htmlFor="expires">Expires In (days, optional)</Label>
              <Input
                id="expires"
                type="number"
                value={formData.expires_in_days}
                onChange={(e) => setFormData({...formData, expires_in_days: e.target.value})}
                placeholder="Leave empty for no expiration"
                min="1"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">Create API Key</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Show New API Key Modal */}
      <Dialog open={showKeyModal} onOpenChange={setShowKeyModal}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
              API Key Created - Save This Now!
            </DialogTitle>
            <DialogDescription>
              This is the only time the full API key will be displayed. Copy it now and store it securely.
            </DialogDescription>
          </DialogHeader>
          
          {newKeyData && (
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-yellow-900 mb-2">⚠️ Important</p>
                <p className="text-sm text-yellow-800">
                  You will not be able to see this API key again. Make sure to copy it now!
                </p>
              </div>
              
              <div>
                <Label>API Key</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={newKeyData.api_key}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button
                    onClick={() => copyToClipboard(newKeyData.api_key)}
                    variant="outline"
                  >
                    {copiedKey ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-600">Name</p>
                  <p className="font-medium">{newKeyData.name}</p>
                </div>
                <div>
                  <p className="text-slate-600">Prefix</p>
                  <p className="font-mono">{newKeyData.key_prefix}...</p>
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button onClick={() => setShowKeyModal(false)}>
                  I've Saved the Key
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminAPIManagement;
